import { ApiError, apiRequest } from "./client";

export type AttachmentState = "UPLOADING" | "QUARANTINED" | "ACTIVE" | "REJECTED";
export type PreviewKind = "image" | "audio" | "video" | "pdf" | "office";

export interface Attachment {
  id: string;
  scope: "PROJECT" | "REGISTRANT_CERTIFICATE";
  state: AttachmentState;
  project_id: string | null;
  registrant_id: string | null;
  original_name: string;
  display_name: string;
  extension: string;
  declared_mime: string;
  detected_mime: string | null;
  size_bytes: number;
  rejection_reason: string | null;
  upload_expires_at: string;
  uploaded_at: string | null;
  scanned_at: string | null;
  created_at: string;
  updated_at: string;
  can_preview: boolean;
}

interface UploadIntent {
  attachment: Attachment;
  upload_url: string;
  method: "PUT";
  headers: Record<string, string>;
  expires_at: string;
}

export interface AttachmentTicket {
  audit_id: string;
  url: string;
  expires_at: string;
  preview_kind: PreviewKind | null;
}

const MIME_FALLBACKS: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  webp: "image/webp",
  mp3: "audio/mpeg",
  mp4: "video/mp4",
  wav: "audio/wav",
  rar: "application/vnd.rar",
  zip: "application/zip",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  doc: "application/msword",
  pdf: "application/pdf",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

function extensionOf(name: string) {
  return name.includes(".") ? name.split(".").pop()!.toLowerCase() : "";
}

function readCookie(name: string): string | undefined {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")
    .slice(1)
    .join("=");
}

async function sha256(file: File): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function fetchProjectAttachments(id: string): Promise<Attachment[]> {
  return apiRequest(`/api/v1/projects/${id}/attachments`);
}

export function fetchCertificateAttachments(id: string): Promise<Attachment[]> {
  return apiRequest(`/api/v1/registrants/${id}/certificate-attachments`);
}

export async function createUploadIntent(
  target: { projectId: string } | { registrantId: string },
  file: File,
): Promise<UploadIntent> {
  const extension = extensionOf(file.name);
  const contentType = file.type || MIME_FALLBACKS[extension] || "application/octet-stream";
  const path =
    "projectId" in target
      ? `/api/v1/projects/${target.projectId}/attachments/upload-intents`
      : `/api/v1/registrants/${target.registrantId}/certificate-attachment/upload-intents`;
  return apiRequest(path, {
    method: "POST",
    body: JSON.stringify({
      original_name: file.name,
      size_bytes: file.size,
      content_type: contentType,
      sha256: await sha256(file),
    }),
  });
}

export function uploadToIntent(
  intent: UploadIntent,
  file: File,
  onProgress: (progress: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", intent.upload_url);
    request.withCredentials = intent.upload_url.startsWith("/");
    Object.entries(intent.headers).forEach(([name, value]) =>
      request.setRequestHeader(name, value),
    );
    if (intent.upload_url.startsWith("/")) {
      const csrf = readCookie("documentation_csrf");
      if (csrf) request.setRequestHeader("X-CSRF-Token", decodeURIComponent(csrf));
    }
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    request.onerror = () => reject(new Error("附件上传网络异常"));
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) resolve();
      else reject(new ApiError(request.status, "UPLOAD_FAILED", "附件上传失败"));
    };
    request.send(file);
  });
}

export function completeUpload(id: string): Promise<Attachment> {
  return apiRequest(`/api/v1/attachments/${id}/complete`, { method: "POST" });
}

export function createPreviewTicket(id: string): Promise<AttachmentTicket> {
  return apiRequest(`/api/v1/attachments/${id}/preview-ticket`, { method: "POST" });
}

export function createDownloadTicket(id: string): Promise<AttachmentTicket> {
  return apiRequest(`/api/v1/attachments/${id}/download-ticket`, { method: "POST" });
}

export function renameAttachment(id: string, displayName: string): Promise<Attachment> {
  return apiRequest(`/api/v1/attachments/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ display_name: displayName }),
  });
}

export function deleteAttachment(id: string): Promise<void> {
  return apiRequest(`/api/v1/attachments/${id}`, { method: "DELETE" });
}
